import { Group, ZoneType } from "@fairfleet/geotab";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { DateTimePicker } from "@mui/x-date-pickers-pro";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ZoneEditor } from "../hooks/useZoneEditor";
import { ZoneDraftError } from "../models/ZoneDraft";
import { colorToCss, cssToColor, defaultColorForTypes } from "../utils/zoneColor";
import { validateZoneDraft } from "../utils/zoneDraft";
import { LngLat, polygonAreaM2 } from "../utils/zoneGeometry";
import { zoneTypeLabel } from "../utils/zoneTypes";
import AddressSearchField from "./AddressSearchField";

const SIZE_SLIDER_MIN_M = 10;
const SIZE_SLIDER_MAX_M = 2000;
const SIZE_INPUT_MAX_M = 10_000;
const EXTERNAL_REFERENCE_MAX = 255;
const SQUARE_METRES_PER_HECTARE = 10_000;

interface Props {
  editor: ZoneEditor;
  types: ZoneType[];
  groups: Group[];
  isNew: boolean;
  saving: boolean;
  onSave(): void;
  onCancel(): void;
  onDelete(): void;
  onFlyTo(center: LngLat): void;
}

export default function ZoneForm({ editor, types, groups, isNew, saving, onSave, onCancel, onDelete, onFlyTo }: Props) {
  const { t } = useTranslation();
  const [colorTouched, setColorTouched] = useState(!isNew);
  const draft = editor.draft;
  if (!draft) return null;

  const errors = validateZoneDraft(draft);
  const has = (error: ZoneDraftError) => errors.includes(error);
  const area = polygonAreaM2([...draft.points, ...draft.points.slice(0, 1)]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <Stack spacing={1.5} sx={{ p: 1.5, overflowY: "auto", flex: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {isNew ? t("zonesPage.form.titleNew") : t("zonesPage.form.titleEdit")}
        </Typography>

        {isNew && (
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            color="primary"
            value={editor.mode}
            onChange={(_, mode) => mode && editor.setMode(mode)}
          >
            <ToggleButton value="address">{t("zonesPage.form.modeAddress")}</ToggleButton>
            <ToggleButton value="draw">{t("zonesPage.form.modeDraw")}</ToggleButton>
          </ToggleButtonGroup>
        )}

        {editor.mode === "address" ? (
          <>
            <AddressSearchField
              label={t("zonesPage.form.address")}
              onSelect={(center, label) => {
                editor.setAddress(center, label);
                onFlyTo(center);
              }}
            />
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              color="primary"
              value={editor.shape}
              onChange={(_, shape) => shape && editor.setShape(shape)}
            >
              <ToggleButton value="circle">{t("zonesPage.form.circle")}</ToggleButton>
              <ToggleButton value="square">{t("zonesPage.form.square")}</ToggleButton>
            </ToggleButtonGroup>
            <Stack direction="row" spacing={2} alignItems="center">
              <Slider
                size="small"
                min={SIZE_SLIDER_MIN_M}
                max={SIZE_SLIDER_MAX_M}
                value={Math.min(editor.sizeM, SIZE_SLIDER_MAX_M)}
                onChange={(_, value) => editor.setSizeM(value as number)}
                aria-label={editor.shape === "circle" ? t("zonesPage.form.radius") : t("zonesPage.form.side")}
              />
              <TextField
                size="small"
                type="number"
                label={editor.shape === "circle" ? t("zonesPage.form.radius") : t("zonesPage.form.side")}
                value={editor.sizeM}
                onChange={e =>
                  editor.setSizeM(Math.max(SIZE_SLIDER_MIN_M, Math.min(SIZE_INPUT_MAX_M, Number(e.target.value) || 0)))
                }
                slotProps={{ htmlInput: { min: SIZE_SLIDER_MIN_M, max: SIZE_INPUT_MAX_M } }}
                sx={{ width: 140, flexShrink: 0 }}
              />
            </Stack>
          </>
        ) : (
          <>
            {isNew && <AddressSearchField label={t("zonesPage.form.moveMapTo")} onSelect={onFlyTo} />}
            <Typography variant="body2" color="text.secondary">
              {editor.drawing ? t("zonesPage.form.drawHint") : t("zonesPage.form.editHint")}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" disabled={draft.points.length === 0} onClick={editor.undoPoint}>
                {t("zonesPage.form.undoPoint")}
              </Button>
              <Button size="small" variant="outlined" disabled={draft.points.length === 0} onClick={editor.resetPoints}>
                {t("zonesPage.form.resetPoints")}
              </Button>
              {editor.drawing && (
                <Button size="small" variant="contained" disabled={draft.points.length < 3} onClick={() => editor.finishDrawing()}>
                  {t("zonesPage.form.finishDrawing")}
                </Button>
              )}
            </Stack>
          </>
        )}

        <Typography variant="caption" color={has("tooFewPoints") ? "error" : "text.secondary"}>
          {has("tooFewPoints")
            ? t("zonesPage.form.errors.tooFewPoints")
            : t("zonesPage.form.shapeInfo", {
                count: draft.points.length,
                area: (area / SQUARE_METRES_PER_HECTARE).toLocaleString("de-DE", { maximumFractionDigits: 2 }),
              })}
        </Typography>

        <TextField
          size="small"
          label={t("zonesPage.form.name")}
          required
          value={draft.name}
          onChange={e => editor.setField("name", e.target.value)}
          error={has("nameRequired")}
          helperText={has("nameRequired") ? t("zonesPage.form.errors.nameRequired") : undefined}
        />

        <Autocomplete<ZoneType, true>
          multiple
          size="small"
          options={types}
          value={types.filter(type => draft.zoneTypeIds.includes(type.id))}
          getOptionLabel={type => zoneTypeLabel(type, t)}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_, value) => {
            const ids = value.map(type => type.id);
            editor.setField("zoneTypeIds", ids);
            if (!colorTouched) editor.setField("fillColor", defaultColorForTypes(ids, types));
          }}
          renderInput={params => (
            <TextField
              {...params}
              required
              label={t("zonesPage.form.types")}
              error={has("typeRequired")}
              helperText={has("typeRequired") ? t("zonesPage.form.errors.typeRequired") : undefined}
            />
          )}
        />

        <Accordion disableGutters variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">{t("zonesPage.form.more")}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              <TextField
                size="small"
                label={t("zonesPage.form.comment")}
                multiline
                value={draft.comment}
                onChange={e => editor.setField("comment", e.target.value)}
              />
              <TextField
                size="small"
                type="color"
                label={t("zonesPage.form.color")}
                value={colorToCss(draft.fillColor)}
                onChange={e => {
                  setColorTouched(true);
                  editor.setField("fillColor", cssToColor(e.target.value, draft.fillColor.a));
                }}
              />
              <Autocomplete<Group, true>
                multiple
                size="small"
                options={groups}
                value={groups.filter(group => draft.groupIds.includes(group.id))}
                getOptionLabel={group => group.name ?? group.id}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, value) => editor.setField("groupIds", value.map(group => group.id))}
                renderInput={params => <TextField {...params} label={t("zonesPage.form.groups")} />}
              />
              <DateTimePicker
                label={t("zonesPage.form.activeFrom")}
                value={draft.activeFrom ? new Date(draft.activeFrom) : null}
                onChange={value => value && !isNaN(value.getTime()) && editor.setField("activeFrom", value.toISOString())}
                slotProps={{ textField: { size: "small" } }}
              />
              <DateTimePicker
                label={t("zonesPage.form.activeTo")}
                value={draft.activeTo ? new Date(draft.activeTo) : null}
                onChange={value => value && !isNaN(value.getTime()) && editor.setField("activeTo", value.toISOString())}
                slotProps={{
                  textField: {
                    size: "small",
                    error: has("activeRange"),
                    helperText: has("activeRange") ? t("zonesPage.form.errors.activeRange") : undefined,
                  },
                }}
              />
              <FormControlLabel
                control={
                  <Switch checked={draft.displayed} onChange={e => editor.setField("displayed", e.target.checked)} />
                }
                label={t("zonesPage.form.displayed")}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={draft.mustIdentifyStops}
                    onChange={e => editor.setField("mustIdentifyStops", e.target.checked)}
                  />
                }
                label={t("zonesPage.form.mustIdentifyStops")}
              />
              <FormHelperText sx={{ mt: -1 }}>{t("zonesPage.form.mustIdentifyStopsHelp")}</FormHelperText>
              <TextField
                size="small"
                label={t("zonesPage.form.externalReference")}
                value={draft.externalReference}
                onChange={e => editor.setField("externalReference", e.target.value.slice(0, EXTERNAL_REFERENCE_MAX))}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
        {errors.length > 0 && <FormHelperText>{t("zonesPage.form.incomplete")}</FormHelperText>}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ p: 1.5, borderTop: 1, borderColor: "divider" }}>
        {!isNew && (
          <Button color="error" onClick={onDelete} disabled={saving}>
            {t("zonesPage.delete")}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onCancel}>{t("zonesPage.cancel")}</Button>
        <Button variant="contained" disabled={errors.length > 0 || saving} onClick={onSave}>
          {t("zonesPage.save")}
        </Button>
      </Stack>
    </Box>
  );
}
